import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import api from '../utils/api';
import toast from 'react-hot-toast';

const COLORS = ['#E74C3C','#E67E22','#27AE60','#2980B9','#8E44AD','#1ABC9C','#F39C12','#9B59B6','#16A085','#2ECC71','#34495E','#3498DB','#E91E63','#95A5A6','#C0392B'];

function StatBox({ label, value, color, icon }) {
  return (
    <div style={{ background:'#fff', borderRadius:10, padding:'16px 20px',
      borderLeft:`4px solid ${color}`, boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
      <div style={{ fontSize:11, color:'#888', fontWeight:600 }}>{icon} {label}</div>
      <div style={{ fontSize:22, fontWeight:700, color, marginTop:4 }}>
        ${Number(value||0).toLocaleString('en-US',{minimumFractionDigits:2})}
      </div>
    </div>
  );
}

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    api.get('/dashboard/reports').then(r => {
      setReports(r.data);
      if (r.data.length) { setSelected(r.data[0].month_key); }
    }).catch(()=>toast.error('Failed to load reports'));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoadingDetail(true);
    api.get(`/dashboard/reports/${selected}`)
      .then(r => setDetail(r.data))
      .catch(()=>toast.error('Failed to load report'))
      .finally(()=>setLoadingDetail(false));
  }, [selected]);

  const handleExportExcel = async () => {
    try {
      const res = await api.get(`/export/excel?month=${selected}`, { responseType:'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href=url;
      a.download=`Budget_Report_${selected}.xlsx`; a.click();
      toast.success('Exported');
    } catch { toast.error('Export failed'); }
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h1 style={{ margin:0, fontSize:22, color:'#1E2A38', fontWeight:700 }}>📋 Monthly Reports</h1>
          <p style={{ margin:'4px 0 0', color:'#888', fontSize:13 }}>Closed month snapshots</p>
        </div>
        {selected && detail && (
          <button onClick={handleExportExcel}
            style={{ padding:'9px 18px', borderRadius:8, border:'1.5px solid #27AE60',
              background:'#fff', color:'#27AE60', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            📥 Export Excel
          </button>
        )}
      </div>

      {reports.length === 0 ? (
        <div style={{ background:'#fff', borderRadius:12, padding:48, textAlign:'center',
          boxShadow:'0 2px 8px rgba(0,0,0,0.07)' }}>
          <div style={{ fontSize:48, marginBottom:12 }}>📋</div>
          <div style={{ fontSize:16, color:'#555', fontWeight:600 }}>No closed months yet</div>
          <div style={{ fontSize:13, color:'#aaa', marginTop:8 }}>
            Close a month from the Dashboard to generate your first report.
          </div>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'200px 1fr', gap:16 }}>
          {/* Month list */}
          <div style={{ background:'#fff', borderRadius:12, padding:12, boxShadow:'0 2px 8px rgba(0,0,0,0.07)', height:'fit-content' }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#888', padding:'4px 8px', marginBottom:4 }}>CLOSED MONTHS</div>
            {reports.map(r => (
              <button key={r.month_key} onClick={()=>setSelected(r.month_key)}
                style={{
                  display:'block', width:'100%', textAlign:'left', padding:'10px 12px',
                  borderRadius:8, border:'none', cursor:'pointer', marginBottom:2,
                  background: selected===r.month_key ? '#1E2A38' : 'transparent',
                  color: selected===r.month_key ? '#fff' : '#333',
                  fontSize:13, fontWeight: selected===r.month_key ? 600 : 400,
                }}>
                <div>{r.month_label}</div>
                <div style={{ fontSize:11, opacity:0.7 }}>
                  Net: {parseFloat(r.net_balance)>=0?'+':''}${Number(r.net_balance).toLocaleString()}
                </div>
              </button>
            ))}
          </div>

          {/* Report detail */}
          <div>
            {loadingDetail ? (
              <div style={{ padding:40, textAlign:'center', color:'#aaa' }}>Loading…</div>
            ) : detail ? (
              <>
                <div style={{ marginBottom:16 }}>
                  <h2 style={{ margin:'0 0 4px', fontSize:18, color:'#1E2A38' }}>{detail.month_label}</h2>
                  <div style={{ fontSize:12, color:'#aaa' }}>
                    Closed: {new Date(detail.closed_at).toLocaleDateString('en-GB', {day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}
                  </div>
                </div>

                {/* KPIs */}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
                  <StatBox label="Income"   value={detail.total_income}   color="#27AE60" icon="💰" />
                  <StatBox label="Expenses" value={detail.total_expenses} color="#E74C3C" icon="💸" />
                  <StatBox label="Savings"  value={detail.total_savings}  color="#8E44AD" icon="🏦" />
                  <StatBox label="Net"      value={detail.net_balance}    color={detail.net_balance>=0?'#2980B9':'#E74C3C'} icon="⚖️" />
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
                  {/* Category breakdown */}
                  <div style={{ background:'#fff', borderRadius:12, padding:20, boxShadow:'0 2px 8px rgba(0,0,0,0.07)' }}>
                    <h3 style={{ margin:'0 0 12px', fontSize:14 }}>🥧 Spending by Category</h3>
                    {detail.category_breakdown?.some(c=>parseFloat(c.spent)>0) ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie data={detail.category_breakdown.filter(c=>parseFloat(c.spent)>0)}
                            dataKey="spent" nameKey="name" cx="50%" cy="50%" outerRadius={75}
                            label={({name,percent})=>percent>0.05?`${(percent*100).toFixed(0)}%`:''} labelLine={false}>
                            {detail.category_breakdown.filter(c=>parseFloat(c.spent)>0).map((_,i)=>
                              <Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                          </Pie>
                          <Tooltip formatter={v=>`$${Number(v).toLocaleString()}`}/>
                        </PieChart>
                      </ResponsiveContainer>
                    ) : <div style={{height:200,display:'flex',alignItems:'center',justifyContent:'center',color:'#aaa'}}>No data</div>}
                  </div>

                  {/* Budget vs Actual */}
                  <div style={{ background:'#fff', borderRadius:12, padding:20, boxShadow:'0 2px 8px rgba(0,0,0,0.07)' }}>
                    <h3 style={{ margin:'0 0 12px', fontSize:14 }}>🎯 Budget vs Actual</h3>
                    <div style={{ maxHeight:200, overflowY:'auto' }}>
                      {detail.budget_vs_actual?.filter(r=>parseFloat(r.budget)>0||parseFloat(r.actual)>0).map((r,i)=>{
                        const pct = r.budget>0 ? Math.min(parseFloat(r.actual)/parseFloat(r.budget),1) : 1;
                        const over = parseFloat(r.actual)>parseFloat(r.budget)&&r.budget>0;
                        return (
                          <div key={i} style={{ marginBottom:10 }}>
                            <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:3 }}>
                              <span>{r.name}</span>
                              <span style={{ color:over?'#E74C3C':'#444' }}>
                                ${Number(r.actual).toLocaleString()} / ${Number(r.budget).toLocaleString()}
                                {over && ' ⚠️'}
                              </span>
                            </div>
                            <div style={{ height:5, background:'#f0f0f0', borderRadius:3 }}>
                              <div style={{ height:5, borderRadius:3, width:`${pct*100}%`,
                                background:over?'#E74C3C':pct>0.8?'#F5A623':'#27AE60' }}/>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Top expenses + Goals snapshot */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                  <div style={{ background:'#fff', borderRadius:12, padding:20, boxShadow:'0 2px 8px rgba(0,0,0,0.07)' }}>
                    <h3 style={{ margin:'0 0 12px', fontSize:14 }}>🔝 Top Expenses</h3>
                    {detail.top_expenses?.map((ex,i)=>(
                      <div key={i} style={{ display:'flex', justifyContent:'space-between',
                        padding:'6px 0', borderBottom:'1px solid #f5f5f5', fontSize:12 }}>
                        <span>
                          <span style={{ background:'#E74C3C', color:'#fff', borderRadius:10,
                            padding:'1px 6px', fontSize:10, marginRight:6 }}>#{i+1}</span>
                          {ex.description||ex.category}
                        </span>
                        <span style={{ fontWeight:600, color:'#E74C3C' }}>${Number(ex.amount_usd).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ background:'#fff', borderRadius:12, padding:20, boxShadow:'0 2px 8px rgba(0,0,0,0.07)' }}>
                    <h3 style={{ margin:'0 0 12px', fontSize:14 }}>🏦 Savings Goals (Snapshot)</h3>
                    {detail.savings_goals_snapshot?.map((g,i)=>{
                      const pct = g.target_usd>0?Math.min(parseFloat(g.saved_usd)/parseFloat(g.target_usd),1):0;
                      return (
                        <div key={i} style={{ marginBottom:10 }}>
                          <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:3 }}>
                            <span>{g.name}</span>
                            <span>${Number(g.saved_usd).toLocaleString()} / ${Number(g.target_usd).toLocaleString()}</span>
                          </div>
                          <div style={{ height:5, background:'#f0f0f0', borderRadius:3 }}>
                            <div style={{ height:5, borderRadius:3, width:`${pct*100}%`, background:'#2ECC71' }}/>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {detail.notes && (
                  <div style={{ background:'#FFF9E6', borderRadius:8, padding:12, marginTop:16,
                    border:'1px solid #F5A623', fontSize:13, color:'#666' }}>
                    📝 <strong>Notes:</strong> {detail.notes}
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
